import { Navigate, useOutletContext } from "react-router-dom";
import { CalendarPlus, CalendarClock, CheckCircle2, Loader2 } from "lucide-react";
import LoadingSkeleton from "../components/LoadingSkeleton";
import { useAuth } from "../context/AuthContext";
import { useQuery } from "../lib/useQuery";
import { getSessions } from "../lib/queries";
import { EMERGENCY_CALENDLY_URL } from "../lib/constants";

const WHEN_TO_USE = [
  "Cuando llevas más de 1 mes desconectado del programa, sin entrar en Skool, sin realizar actividades, sin asistir a sesiones o sin pedir acompañamiento individual.",
  "Cuando aparece una situación complicada que supone un riesgo importante para tu negocio y no sabes cómo afrontarla.",
  "Cuando sientes que has perdido el foco, la claridad o el rumbo después de una etapa de dispersión.",
];

// Una "Llamada de Emergencia" agendada se registra en Airtable (Llamadas
// Alumnos) con un tipo que contiene "Emergencia". Si aparece entre las
// sesiones del alumno, ya la ha consumido.
function isEmergencySession(session) {
  return /emergencia/i.test(session?.type || "");
}

function formatLongDate(iso) {
  if (!iso) return "";
  return new Date(`${iso}T00:00`).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function Emergencia() {
  const { student } = useOutletContext();
  const { user } = useAuth();
  const { data: sessions, loading } = useQuery(
    () => getSessions(user.id),
    [user.id]
  );

  if (!student) return <LoadingSkeleton />;

  // Solo los alumnos con registro en Airtable pueden usarla: Make valida el
  // email contra Airtable, así que sin airtable_id la reserva sería rechazada.
  const isAirtable =
    student.airtable_id && !student.airtable_id.startsWith("admin_");
  if (!isAirtable) return <Navigate to="/" replace />;

  const emergencySession = (sessions ?? []).find(isEmergencySession);
  const used = Boolean(emergencySession);

  // Comparamos fecha + hora de la llamada con el momento actual para decidir
  // si está por venir ("será el día X") o ya ocurrió ("fue el día X").
  const sessionDateTime = emergencySession?.date
    ? new Date(`${emergencySession.date}T${emergencySession.time || "00:00"}`)
    : null;
  const isPast = sessionDateTime ? sessionDateTime.getTime() < Date.now() : false;
  const dateLabel = formatLongDate(emergencySession?.date);
  const timeLabel = emergencySession?.time ? ` a las ${emergencySession.time}h` : "";

  return (
    <div className="animate-fade-in max-w-3xl mx-auto space-y-6">
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-red-50 to-orange-50 border-b border-red-100 px-6 py-5 flex items-center gap-3">
          <span className="text-2xl">🚨</span>
          <h2 className="text-xl font-bold text-gray-900">
            Llamada de Emergencia
          </h2>
        </div>

        <div className="p-6 space-y-4 text-sm text-gray-600 leading-relaxed">
          <p>
            Dentro del programa tendrás disponible una{" "}
            <strong className="text-gray-900">“Llamada de Emergencia”</strong>,
            pensada para esos momentos en los que necesitas parar, ordenar y
            volver a avanzar con claridad.
          </p>

          <div>
            <p className="font-semibold text-gray-900 mb-2">
              ¿Cuándo tiene sentido usarla?
            </p>
            <ul className="space-y-2">
              {WHEN_TO_USE.map((item, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-brand-accent mt-1">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <p>
            El objetivo de esta llamada no es “rescatarte”, sino ayudarte a
            reconducir la situación y volver a responsabilizarte de tu progreso.
          </p>
          <p>
            Porque entendemos que emprender tiene momentos de bloqueo,
            saturación o desconexión. Y precisamente por eso queremos que tengas
            una herramienta que te permita retomar el camino antes de seguir
            alejándote de tus objetivos.
          </p>
          <p>
            El programa, las herramientas y el acompañamiento están aquí. Pero
            dar el paso para volver a activarte depende de ti.
          </p>
          <p className="font-semibold text-gray-900">
            Responsabilidad. Claridad. Acción.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8 text-gray-400">
          <Loader2 size={20} className="animate-spin" />
        </div>
      ) : used ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 text-center space-y-3">
          {isPast ? (
            <CheckCircle2 size={32} className="mx-auto text-emerald-500" />
          ) : (
            <CalendarClock size={32} className="mx-auto text-brand-accent" />
          )}
          <h3 className="font-semibold text-gray-900">
            {isPast
              ? `Tu llamada de emergencia fue el ${dateLabel}`
              : `Tu llamada de emergencia será el ${dateLabel}${timeLabel}`}
          </h3>
          <p className="text-sm text-gray-600 leading-relaxed max-w-lg mx-auto">
            Recuerda que es una herramienta de un solo uso. Responsabilidad.
            Claridad. Acción.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 text-center space-y-4">
          <p className="text-sm text-gray-500">
            Dispones de <strong className="text-gray-900">una</strong> Llamada
            de Emergencia. Reserva el horario que mejor te venga.
          </p>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 max-w-lg mx-auto">
            Al agendar, recuerda usar el mismo email con el que estás dado de
            alta en la plataforma.
          </p>
          <a
            href={EMERGENCY_CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-brand hover:bg-brand-dark text-white text-sm font-semibold transition-all"
          >
            <CalendarPlus size={16} />
            Agendar mi Llamada de Emergencia
          </a>
        </div>
      )}
    </div>
  );
}
